<script setup>
import { computed, ref, watch } from "vue";
import FriendCard from "@/Components/Profile/FriendCard.vue";
import ListsPagination from "@/Components/Profile/ListsPagination.vue";
import SortDropdown from "@/Components/Profile/SortDropdown.vue";

/**
 * Cream panel with the friends grid shared by the Followers and Following
 * pages (GH-851): optional leading action slot, centered title, sort +
 * pagination.
 */
const props = defineProps({
    people: {
        type: Array,
        required: true,
    },
    title: {
        type: String,
        required: true,
    },
    emptyMessage: {
        type: String,
        required: true,
    },
});

const PEOPLE_SORT_OPTIONS = [
    { value: "recent", label: "Most Recent" },
    { value: "name-asc", label: "Name A–Z" },
    { value: "name-desc", label: "Name Z–A" },
];

const PER_PAGE = 12;

const sort = ref("recent");
const page = ref(1);

const sorted = computed(() => {
    const copy = [...props.people];

    switch (sort.value) {
        case "name-asc":
            return copy.sort((a, b) =>
                a.presentation_name.localeCompare(b.presentation_name),
            );
        case "name-desc":
            return copy.sort((a, b) =>
                b.presentation_name.localeCompare(a.presentation_name),
            );
        default:
            // Most recent: the server orders rows newest-first already.
            return copy;
    }
});

const pages = computed(() =>
    Math.max(1, Math.ceil(sorted.value.length / PER_PAGE)),
);

const paged = computed(() =>
    sorted.value.slice((page.value - 1) * PER_PAGE, page.value * PER_PAGE),
);

watch(sort, () => (page.value = 1));
</script>

<template>
    <section
        class="rounded-brand bg-brand-cream px-4 py-6 sm:px-[46px] sm:py-8"
    >
        <header
            class="flex flex-col gap-4 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center"
        >
            <div class="justify-self-start max-lg:order-2">
                <slot name="action" />
            </div>
            <h2
                class="text-center font-brand text-[25px] font-bold text-[#102a3d]"
            >
                {{ title }}
            </h2>
            <SortDropdown
                v-model="sort"
                :options="PEOPLE_SORT_OPTIONS"
                class="lg:justify-self-end"
            />
        </header>

        <template v-if="people.length">
            <div
                class="mt-7 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
            >
                <FriendCard
                    v-for="person in paged"
                    :key="person.uuid"
                    :user="person"
                />
            </div>

            <ListsPagination
                v-if="pages > 1"
                v-model:page="page"
                :pages="pages"
                class="mt-10"
            />
        </template>

        <div v-else class="flex flex-col items-center gap-5 py-14 text-center">
            <p class="max-w-md font-brand text-[16px] text-[#565a77]">
                {{ emptyMessage }}
            </p>
        </div>
    </section>
</template>
